import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { UserProfile } from "@/types/user";
import { useState } from "react";

interface ProfessionalInfoProps {
  profile: UserProfile;
  isEditing: boolean;
  onUpdate: (field: string, value: string) => void;
  onAddSpecialty: (specialty: string) => void;
  onRemoveSpecialty: (index: number) => void;
}

export function ProfessionalInfo({
  profile,
  isEditing,
  onUpdate,
  onAddSpecialty,
  onRemoveSpecialty
}: ProfessionalInfoProps) {
  const [newSpecialty, setNewSpecialty] = useState('');

  const handleAddSpecialty = () => {
    if (newSpecialty.trim()) {
      onAddSpecialty(newSpecialty.trim());
      setNewSpecialty('');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Informações Profissionais</h2>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Cargo/Função</label>
          {isEditing ? (
            <Input
              value={profile.role}
              onChange={(e) => onUpdate('role', e.target.value)}
              placeholder="Ex: Eletricista, Encanador, etc."
            />
          ) : (
            <p className="mt-1 text-gray-900">{profile.role || 'Não informado'}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Especialidades</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {profile.specialties?.map((specialty, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {specialty}
                {isEditing && (
                  <button
                    onClick={() => onRemoveSpecialty(index)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          {isEditing && (
            <div className="mt-2 flex gap-2">
              <Input
                value={newSpecialty}
                onChange={(e) => setNewSpecialty(e.target.value)}
                placeholder="Nova especialidade"
                onKeyPress={(e) => e.key === 'Enter' && handleAddSpecialty()}
              />
              <Button type="button" onClick={handleAddSpecialty}>
                Adicionar
              </Button>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Serviços Oferecidos</label>
          {isEditing ? (
            <Textarea
              value={profile.services}
              onChange={(e) => onUpdate('services', e.target.value)}
              placeholder="Descreva os serviços que você oferece"
              className="mt-1"
            />
          ) : (
            <p className="mt-1 text-gray-900">{profile.services || 'Não informado'}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Sobre Mim</label>
          {isEditing ? (
            <Textarea
              value={profile.description}
              onChange={(e) => onUpdate('description', e.target.value)}
              placeholder="Fale um pouco sobre você e sua experiência profissional"
              className="mt-1"
            />
          ) : (
            <p className="mt-1 text-gray-900">{profile.description || 'Não informado'}</p>
          )}
        </div>
      </div>
    </div>
  );
}